import type { BrainData, BrainRule, KnowledgeBaseData, TrainRow } from './types';

/** AI Brain › General (SettingsBrain board). */
export const BRAIN: BrainData = {
  purposes: [
    {
      rank: 1, label: 'Clinical safety', raisedBy: 'Appointment Reminders · post-op',
      note: 'If the patient is not reached, this is the only purpose that raises a human task rather than going quiet.',
    },
    {
      rank: 2, label: 'Same-day opening', raisedBy: 'Gap Filler',
      note: 'May be moved down, never up. It must never outrank a post-op check.',
    },
    { rank: 3, label: 'Appointment confirmation', raisedBy: 'Appointment Reminders' },
    { rank: 4, label: 'Pre-visit preparation', raisedBy: 'Appointment Reminders' },
    { rank: 5, label: 'Outstanding balance', raisedBy: 'Accounts receivable' },
    { rank: 6, label: 'Broken appointment follow-up', raisedBy: 'Appointment Reminders + Gap Filler' },
    { rank: 7, label: 'Treatment follow-up', raisedBy: 'Treatment Coordinator' },
    { rank: 8, label: 'Invisalign follow-up', raisedBy: 'Invisalign Coordinator' },
    {
      rank: 9, label: 'Scheduled recall', raisedBy: 'Recall',
      note: 'Holds one position against everything outside Recall. Campaigns are ordered among themselves, below.',
      campaigns: [
        { rank: 1, name: 'Adult hygiene', interval: '6 month interval' },
        { rank: 2, name: 'Perio maintenance', interval: '3 month interval' },
        { rank: 3, name: 'Pediatric hygiene', interval: '6 month interval' },
        { rank: 4, name: 'Implant check', interval: '12 month interval' },
      ],
    },
    {
      rank: 10, label: 'Lapsed patient outreach', raisedBy: 'Recall',
      note: 'No current relationship with the practice — a different consent and tone posture from routine recall.',
    },
    { rank: 11, label: 'Review ask', raisedBy: 'Appointment Reminders' },
    {
      // Renamed from Loyalty (amendment 32 §2).
      rank: 12, label: 'Birthday and thank-you', raisedBy: 'Recall',
    },
  ],
  tieBreakers: ['higher rank wins', 'soonest expiry', 'smaller substitutable audience', 'oldest intent'],
  name: 'Elva',
  voice: 'Gleam',
  voiceGroups: [
    { group: 'North American', voices: [{ name: 'Gleam', description: 'Feminine' }, { name: 'Meridian', description: 'Masculine' }] },
    { group: 'Southern U.S.', voices: [{ name: 'Delta', description: 'Feminine' }, { name: 'Cinder', description: 'Masculine' }] },
    { group: 'British', voices: [{ name: 'Vesper', description: 'Masculine' }] },
    { group: 'Irish', voices: [{ name: 'Willow', description: 'Feminine' }, { name: 'Stone', description: 'Masculine' }] },
    { group: 'Australian', voices: [{ name: 'Quartz', description: 'Feminine' }, { name: 'Ripple', description: 'Masculine' }] },
    { group: 'Filipino', voices: [{ name: 'Beacon', description: 'Masculine' }] },
    { group: 'Brazilian Portuguese', voices: [{ name: 'Bossa', description: 'Feminine' }, { name: 'Tempo', description: 'Masculine' }] },
  ],
  warmth: 68,
  formality: 42,
  respondInPatientLanguage: true,
  priceQuoting: 'starting_at',
  brandColour: '#4C5BD4',
  hardLimits: [
    {
      id: 'h1', label: 'Never give medical advice or a diagnosis',
      desc: 'Every clinical question goes to a provider. ELVA does not interpret a symptom, suggest a treatment or judge how serious something is.',
    },
    {
      id: 'h2', label: 'Never recommend a medication or a dose',
      desc: 'Even “take ibuprofen for the pain” goes to a provider or the on-call number.',
    },
    {
      id: 'h3', label: 'Never decide how urgent a clinical report is',
      desc: 'ELVA records what the patient said and routes it. Ranking one patient’s pain against another’s is a clinical judgement.',
    },
    {
      id: 'h4', label: 'Never reassure about a symptom',
      desc: 'Not “that’s normal”, not “that’s expected” — whatever the patient describes.',
    },
    {
      id: 'h5', label: 'Never promise an insurance coverage outcome',
      desc: 'ELVA says “we’ll verify with your plan”, never “you’re covered”.',
    },
    {
      id: 'h6', label: 'Never screen or filter who gets asked for a review',
      desc: 'Everyone eligible is asked, or nobody is. Choosing who to ask is review gating, and it is illegal in several places.',
    },
    {
      id: 'h7', label: 'Always redirect a life-threatening emergency to 911',
      desc: 'Chest pain, difficulty breathing, severe trauma or stroke signs get an immediate 911 redirect.',
    },
    {
      id: 'h8', label: 'Always refer self-harm or crisis signals to 988 and a person',
      desc: 'ELVA stops scheduling, gives the 988 Suicide & Crisis Lifeline and hands off to staff.',
    },
  ],
  hipaa: [
    {
      id: 'p1', label: 'Verify identity before sharing anything from the chart', enabled: true,
      desc: 'Before ELVA gives appointment details, a balance or anything from the chart, it confirms a name plus one verifier — date of birth, phone or ZIP.',
    },
    {
      id: 'p2', label: 'Include patient names in outbound voicemails', enabled: false,
      desc: 'A name in a voicemail is a disclosure if someone else hears it. Off means “calling from Alamo Heights Dental about an appointment.”',
    },
    {
      id: 'p3', label: 'Share patient information with anyone other than the patient', enabled: false,
      desc: 'A spouse or parent calling about someone else. Off means ELVA shares only with a contact the patient has authorised in writing.',
    },
  ],
  money: [
    {
      id: 'm1', label: 'Quote a specific fee without verifying insurance', enabled: false,
      desc: 'Off means ELVA gives a range and offers to check what this patient actually owes. On lets it quote the standard fee directly.',
    },
    {
      id: 'm2', label: 'Discuss an outstanding balance over the phone', enabled: false,
      desc: 'Off means “there is a balance — the front desk can walk you through it at your visit.” On lets ELVA say the amount.',
    },
  ],
  recording: [
    {
      id: 'r1', label: 'Record calls', enabled: true,
      desc: 'Encrypted. How long a recording is kept is set by ELVA, by what the call turned out to be — not a flat 90 days.',
    },
    {
      id: 'r2', label: 'Announce the recording and wait for consent', enabled: true,
      desc: 'ELVA says it is recording at the start of the call and pauses. Required by law where two-party consent applies — if this location is in one of the states below, switching this off puts the practice outside the law.',
    },
    {
      id: 'r3', label: 'Say it is an AI on the first interaction', enabled: true,
      desc: '“I’m Elva, the AI receptionist at Alamo Heights Dental.” Best practice everywhere and required in a growing number of places.',
    },
  ],
  twoPartyConsentStates: ['CA', 'FL', 'IL', 'MD', 'MA', 'MT', 'NV', 'NH', 'PA', 'WA'],
  // amendment 32 (simplified Brain)
  locationState: 'TX',
  contactLimit: 3,
  combineMessages: true,
  neverCounted: [
    'A reply to a patient who wrote first',
    'Appointment confirmations and pre-visit messages',
    'Post-op checks',
    'Same-day openings',
    'Calls your team makes from Calls',
  ],
  sayBalance: true,
  languages: [
    { id: 'en', label: 'English', channels: 'Texts, emails and AI calls', patients: 3902, locked: true },
    { id: 'es', label: 'Spanish · Español', channels: 'Texts, emails and AI calls', patients: 418 },
    { id: 'fa', label: 'Farsi · فارسی', channels: 'Texts and emails · AI calls not yet', patients: 86 },
  ],
  languagesAvailable: ['Vietnamese', 'Chinese', 'Arabic', 'Korean', 'Tagalog', 'Portuguese', 'French'],
  stopStats: { textsOff: 2, stoppedTopic: 1 },
};

/** Your rules (amendment 32 §3, board BR2Train card 01) — five, one needs a change. */
export const BRAIN_RULES: BrainRule[] = [
  {
    id: 'r1', text: 'Never book Dr. Alvarez on a Monday — she’s only in Tuesdays and Thursdays.',
    readsAs: 'Doesn’t offer Dr. Alvarez on Monday, Wednesday or Friday, when booking on any channel.',
    appliesTo: 'Every agent', addedBy: 'Ava Thompson', addedAt: '12 Sep', usedCount: 7, status: 'active',
  },
  {
    id: 'r2', text: 'Always mention nitrous to patients who say they’re nervous.',
    readsAs: 'When a patient says they’re nervous, anxious or scared, says nitrous oxide is available and asks if they’d like it noted.',
    appliesTo: 'Every agent', addedBy: 'Ava Thompson', addedAt: '12 Sep', usedCount: 12, status: 'active',
  },
  {
    id: 'r3', text: 'New patients see the dentist before their first cleaning.',
    readsAs: 'When a new patient asks for a cleaning, books a New Patient Exam instead and says why.',
    appliesTo: 'AI Receptionist', addedBy: 'Dana Ruiz', addedAt: '18 Sep', usedCount: 4, status: 'active',
  },
  {
    id: 'r4', text: 'Don’t text patients on Sundays.',
    readsAs: 'No texts ELVA starts on Sundays. Replies to a patient who texts first still go.',
    appliesTo: 'Every agent', addedBy: 'Ava Thompson', addedAt: '20 Sep', status: 'active',
  },
  {
    id: 'r5', text: 'Tell patients we waive the no-show fee the first time.',
    readsAs: 'A fee decision, which ELVA can’t make on its own.',
    appliesTo: 'Accounts receivable', addedBy: 'Maria Lopez', addedAt: '29 Sep', status: 'needs_change',
    fix: { note: 'Put it in the Knowledge base under Policies, and ELVA will say it.', sentence: 'The first missed visit has no fee', category: 'policies' },
  },
];

/** Train ELVA (SettingsTrain board) — 6 + 18 + 5 = 29 waiting. */
export const TRAIN_ROWS: TrainRow[] = [
  // 01 — answers to check (6)
  {
    id: 'a1', kind: 'answer_to_check', waitedDays: 9, channel: 'sms', patientName: 'Rachel Kim',
    patientSaid: 'How much is a cleaning if I’m a new patient?', answeredFromEntry: 'New patient pricing',
    raisedBecause: 'weak_match', raisedDetail: 'that entry is about the exam, not the hygiene visit',
  },
  {
    id: 'a2', kind: 'answer_to_check', waitedDays: 4, channel: 'email', patientName: 'Marcus Webb',
    patientSaid: 'My gum is still bleeding two days after the extraction — is that normal?',
    answeredFromEntry: 'After a surgical extraction', raisedBecause: 'clinical',
    raisedDetail: 'and that entry was last edited 14 months ago',
  },
  {
    id: 'a3', kind: 'answer_to_check', waitedDays: 3, channel: 'call', patientName: 'Dolores Park',
    patientSaid: 'Can I still be seen if my insurance lapsed last month?', answeredFromEntry: 'Plans we take',
    raisedBecause: 'weak_match', raisedDetail: 'the entry lists carriers and says nothing about a lapse',
  },
  {
    id: 'a4', kind: 'answer_to_check', waitedDays: 2, channel: 'sms', patientName: 'Aaron Bell',
    patientSaid: 'Is it ok to take ibuprofen with the antibiotic you gave me?',
    answeredFromEntry: 'After a surgical extraction', raisedBecause: 'clinical',
    raisedDetail: 'and the question is about a medicine the entry does not name',
  },
  {
    id: 'a5', kind: 'answer_to_check', waitedDays: 2, channel: 'email', patientName: 'Priya Raman',
    patientSaid: 'Do you do payment plans for a crown?', answeredFromEntry: 'Payment plans',
    raisedBecause: 'weak_match', raisedDetail: 'the entry names a balance threshold, not a procedure',
  },
  {
    id: 'a6', kind: 'answer_to_check', waitedDays: 1, channel: 'call', patientName: 'Nina Alvarez',
    patientSaid: 'Should the temporary crown feel loose?', answeredFromEntry: 'Crowns',
    raisedBecause: 'clinical', raisedDetail: 'the entry is about price, not about what a temporary should feel like',
  },
  // 02 — questions with no answer (18; five shown)
  { id: 'q1', kind: 'unanswered_question', waitedDays: 8, question: 'Do you take HSA cards?', askedCount: 6, elvaDid: 'ELVA took a message', lastAsked: '8 days ago' },
  { id: 'q2', kind: 'unanswered_question', waitedDays: 3, question: 'Is there parking for a wheelchair van?', askedCount: 4, elvaDid: 'ELVA transferred to the front desk', lastAsked: '3 days ago' },
  { id: 'q3', kind: 'unanswered_question', waitedDays: 3, question: 'Do you see kids under three?', askedCount: 4, elvaDid: 'ELVA took a message', lastAsked: '3 days ago' },
  { id: 'q4', kind: 'unanswered_question', waitedDays: 2, question: 'Do you have evening hours?', askedCount: 3, elvaDid: 'ELVA transferred to the front desk', lastAsked: '2 days ago' },
  { id: 'q5', kind: 'unanswered_question', waitedDays: 1, question: 'What time does the hygienist start on Saturdays?', askedCount: 1, elvaDid: 'ELVA took a message', lastAsked: 'yesterday' },
  { id: 'q6', kind: 'unanswered_question', waitedDays: 1, question: 'Do you offer sedation for anxious patients?', askedCount: 3, elvaDid: 'ELVA took a message', lastAsked: 'yesterday' },
  { id: 'q7', kind: 'unanswered_question', waitedDays: 1, question: 'Is the building accessible by lift?', askedCount: 2, elvaDid: 'ELVA transferred to the front desk', lastAsked: 'yesterday' },
  { id: 'q8', kind: 'unanswered_question', waitedDays: 1, question: 'Do you treat TMJ?', askedCount: 2, elvaDid: 'ELVA took a message', lastAsked: 'yesterday' },
  { id: 'q9', kind: 'unanswered_question', waitedDays: 1, question: 'Can I bring a support person into the room?', askedCount: 2, elvaDid: 'ELVA took a message', lastAsked: 'yesterday' },
  { id: 'q10', kind: 'unanswered_question', waitedDays: 1, question: 'Do you take Medicare?', askedCount: 2, elvaDid: 'ELVA transferred to the front desk', lastAsked: 'yesterday' },
  { id: 'q11', kind: 'unanswered_question', waitedDays: 1, question: 'How early should I arrive for a first visit?', askedCount: 2, elvaDid: 'ELVA took a message', lastAsked: 'yesterday' },
  { id: 'q12', kind: 'unanswered_question', waitedDays: 1, question: 'Do you whiten with strips or trays?', askedCount: 1, elvaDid: 'ELVA took a message', lastAsked: 'yesterday' },
  { id: 'q13', kind: 'unanswered_question', waitedDays: 1, question: 'Is there a student discount?', askedCount: 1, elvaDid: 'ELVA took a message', lastAsked: 'yesterday' },
  { id: 'q14', kind: 'unanswered_question', waitedDays: 1, question: 'Do you do same-day crowns?', askedCount: 1, elvaDid: 'ELVA transferred to the front desk', lastAsked: 'yesterday' },
  { id: 'q15', kind: 'unanswered_question', waitedDays: 1, question: 'Can I get a copy of my x-rays?', askedCount: 1, elvaDid: 'ELVA took a message', lastAsked: 'yesterday' },
  { id: 'q16', kind: 'unanswered_question', waitedDays: 1, question: 'Do you have Saturday emergency cover?', askedCount: 1, elvaDid: 'ELVA took a message', lastAsked: 'yesterday' },
  { id: 'q17', kind: 'unanswered_question', waitedDays: 1, question: 'Is nitrous available for children?', askedCount: 1, elvaDid: 'ELVA took a message', lastAsked: 'yesterday' },
  { id: 'q18', kind: 'unanswered_question', waitedDays: 1, question: 'Do you accept walk-ins?', askedCount: 1, elvaDid: 'ELVA transferred to the front desk', lastAsked: 'yesterday' },
  // 03 — close-call bookings (5)
  {
    id: 'b1', kind: 'close_call_booking', waitedDays: 6,
    patientSaid: 'I need to come in about the crown Dr. Carter started',
    bookedReason: 'Crown Prep', runnerUpReason: 'Crown Seat', queuedBecause: 'close_call',
  },
  {
    id: 'b2', kind: 'close_call_booking', waitedDays: 4, patientSaid: 'My gums bleed when I brush',
    bookedReason: 'Adult Cleaning', runnerUpReason: 'Perio Maintenance', queuedBecause: 'close_call',
  },
  {
    id: 'b3', kind: 'close_call_booking', waitedDays: 3, patientSaid: 'I chipped my front tooth on a fork',
    bookedReason: 'Emergency Visit', queuedBecause: 'nothing_near',
  },
  {
    id: 'b4', kind: 'close_call_booking', waitedDays: 2,
    patientSaid: 'Can I bring my daughter for a clean at the same time as mine?',
    bookedReason: 'Adult Cleaning', queuedBecause: 'nothing_near',
  },
  {
    id: 'b5', kind: 'close_call_booking', waitedDays: 1, patientSaid: 'The filling you did last year came out',
    bookedReason: 'Filling', runnerUpReason: 'Emergency Visit', queuedBecause: 'close_call',
  },
];

/**
 * Knowledge base (amendment 32 §4) — 16 entries in the eight categories, with
 * Office hours / Phone / Parking removed (they live in the read-only Practice
 * management block now). Category counts: Services 2 · Fees 2 · Insurance 3 ·
 * Policies 3 · Providers 2 · Forms 1 · Frequently asked 3 = 16.
 */
export const KNOWLEDGE_BASE: KnowledgeBaseData = {
  answeredFromEntry: 412,
  couldNotAnswer: 18,
  practiceFacts: {
    officeHours: 'Mon–Thu 8:00–5:00 · Fri 8:00–2:00',
    address: '4501 Broadway, San Antonio, TX',
    phone: '(210) 555-0140',
    doctors: 'Dr. Carter · Dr. Alvarez',
  },
  entries: [
    { id: 'k4', category: 'services', title: 'What we offer', body: 'General and cosmetic dentistry, hygiene, implants, clear aligners, emergency care.', enabled: true, updatedAt: '12 Sep', usedCount: 8 },
    {
      id: 'k5', category: 'services', title: 'Invisalign at Alamo Heights',
      body: 'Free scan and simulation. Most cases 12–18 months.', enabled: true, updatedAt: '12 Sep', usedCount: 6,
      alsoAsk: '“How long does Invisalign take?” · “Do you do clear braces?”',
      lockedSentence: 'Most cases 12–18 months.',
      lockedNote: 'The Invisalign Coordinator never gives a timeline — it says a consult tells the patient how long theirs would take.',
    },
    { id: 'k6', category: 'fees', title: 'Adult cleaning', body: '$120 without insurance. Most PPO plans cover it in full twice a year.', enabled: true, updatedAt: '12 Sep', usedCount: 41 },
    { id: 'k7', category: 'fees', title: 'Crowns', body: '$1,150–$1,400 depending on the material.', enabled: true, updatedAt: '12 Sep', usedCount: 9 },
    { id: 'k8', category: 'insurance', title: 'Plans we take', body: 'Delta Dental PPO, Cigna, Aetna, MetLife, Guardian, United Concordia.', enabled: true, updatedAt: '12 Sep', usedCount: 33 },
    { id: 'k9', category: 'insurance', title: 'Payment plans', body: 'CareCredit, and an in-house plan for balances over $500.', enabled: true, updatedAt: '12 Sep', usedCount: 14, alsoAsk: '“Do you take CareCredit?”' },
    { id: 'k16', category: 'insurance', title: 'Do you take FSA?', body: 'Yes — an FSA card works like any other card for the part you owe.', enabled: true, updatedAt: '18 Sep', usedCount: 5 },
    { id: 'k10', category: 'policies', title: 'Cancellations', body: '24 hours’ notice. A missed visit without notice may carry a $50 fee.', enabled: true, updatedAt: '12 Sep', usedCount: 22 },
    { id: 'k11', category: 'policies', title: 'Holiday hours', body: 'Closed 24 December to 2 January.', enabled: false, updatedAt: '12 Sep', usedCount: 0 },
    { id: 'k17', category: 'policies', title: 'Late arrivals', body: 'More than 15 minutes late may need to rebook, so the next patient isn’t held up.', enabled: true, updatedAt: '20 Sep', usedCount: 3 },
    { id: 'k12', category: 'providers', title: 'Dr. Carter', body: 'General and cosmetic. With the practice since 2014.', enabled: true, updatedAt: '12 Sep', usedCount: 7 },
    { id: 'k13', category: 'providers', title: 'Dr. Alvarez', body: 'Implants and oral surgery. Tuesdays and Thursdays.', enabled: true, updatedAt: '12 Sep', usedCount: 4 },
    { id: 'k14', category: 'forms', title: 'New patient forms', body: 'Sent by text before the visit, or on paper fifteen minutes early.', enabled: true, updatedAt: '12 Sep', usedCount: 11 },
    { id: 'k15', category: 'faq', title: 'Do you see emergencies same-day?', body: 'Yes — call before 11:00 and we will find a slot that day.', enabled: true, updatedAt: '12 Sep', usedCount: 18 },
    { id: 'k18', category: 'faq', title: 'Where do I park?', body: 'Free lot behind the building, entrance on Cypress. Two accessible bays by the door.', enabled: true, updatedAt: '12 Sep', usedCount: 12 },
    { id: 'k19', category: 'faq', title: 'Do you see children?', body: 'Yes, from age three. Younger than that we’re happy to refer to a pediatric specialist.', enabled: true, updatedAt: '15 Sep', usedCount: 6 },
  ],
};
